/**
 * Navbar Component
 * Main sidebar navigation component that provides collapsible navigation
 * with organization switching, role-based menu items, and responsive design
 */

// React core imports
import React, { useEffect, useState, useMemo, useCallback } from "react";

// React Router hooks for navigation and routing
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

// NextUI components for UI elements
import { Divider, Tooltip, Accordion, AccordionItem } from "@nextui-org/react";

// Heroicons for chevron icons
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";

// Custom components
import Header from "../Header/Header";
import { getOnedata } from "../../controllers/APIController";
import useGetAllOrganization from "../../hooks/useGetAllOrganization";
import OrgPopUp from "../CreateOrg/OrgPopUp";
import NavbarFooter from "./NavbarFooter";


// Import navigation lists based on environment type (ESG vs SOC2)
const isESG = process.env.REACT_APP_TYPE !== "SOC2";
const {
  dashboardSection,
  collateSection,
  contextualiseSection,
  complySection,
  adminSection,
} = isESG
  ? require("../../constant/Navbar/navbar.constant")
  : require("../../constant/Navbar/soc-navbar.constant");

/**
 * Renders individual navigation items with support for nested sub-items
 * Handles both collapsed and expanded states with proper styling
 * 
 * @param {Object} props - Component props
 * @param {Object} props.data - Navigation item data object
 * @param {string} props.currentActiveUrl - Currently active URL for highlighting
 * @param {string} props.orgId - Organization ID for routing
 * @param {boolean} props.isCollapsed - Whether navbar is collapsed
 * @param {number} props.level - Nesting level for indentation (default: 0)
 * @returns {JSX.Element} Navigation item component
 */
const RenderNavItem = ({ data, currentActiveUrl, orgId, isCollapsed, level = 0 }) => {
  // Check if this item is currently active
  const isVerified = `/${currentActiveUrl}` === data.path;
  const hasSubItems = data.hasSubItems && data.subItems && data.subItems.length > 0;
  
  // Render collapsed state with tooltip
  if (isCollapsed) {
    return (
      <Tooltip content={data.title} placement="right">
        <span
          className={`flex p-1 items-center gap-2 rounded-md px-2 text-sm hover:bg-primary-50 hover:opacity-80 text-neutral-900 hover:text-primary-700 transition-colors ${
            isVerified && "bg-primary-600 text-white"
          }`}
        >
          {data.icon}
        </span>
      </Tooltip>
    );
  }

  // Render accordion for items with sub-items
  if (hasSubItems) {
    return (
      <Accordion
        key={data.id}
        variant="light"
        className="px-0"
        itemClasses={{
          title: "text-sm font-medium",
          trigger: "px-2 py-1 hover:bg-primary-50 rounded-md transition-colors",
          content: "px-0",
        }}
      >
        <AccordionItem
          key={data.id}
          aria-label={data.title}
          title={
            <div className="flex items-center gap-2">
              {data.icon}
              <span className="text-sm">{data.title}</span>
            </div>
          }
          className="px-0"
        >
          {/* Render nested sub-items with increased indentation */}
          <div className="ml-4 space-y-1">
            {data.subItems.map((subItem, subIndex) => (
              <RenderNavItem
                key={subIndex}
                data={subItem}
                currentActiveUrl={currentActiveUrl}
                orgId={orgId}
                isCollapsed={isCollapsed}
                level={level + 1}
              />
            ))}
          </div>
        </AccordionItem>
      </Accordion>
    );
  }

  // Render regular navigation link
  return (
    <NavLink
      className="rounded-md"
      to={`/${orgId}${data.path}`}
      id={data.id}
    >
      <span
        className={`flex p-1 items-center gap-2 rounded-md px-2 text-sm hover:bg-primary-50 text-neutral-900 hover:text-primary-700 transition-colors ${
          isVerified && "bg-primary-600 text-white font-medium"
        }`}
        style={{ paddingLeft: `${level * 12 + 8}px` }}
      >
        {data.icon}
        {data.title}
      </span>
    </NavLink>
  );
};

/**
 * Renders a list of navigation items with optional section title
 * Groups navigation items into logical sections
 * 
 * @param {Object} props - Component props
 * @param {Array} props.List - Array of navigation items to render
 * @param {string} props.currentActiveUrl - Currently active URL
 * @param {string} props.orgId - Organization ID for routing
 * @param {boolean} props.isCollapsed - Whether navbar is collapsed
 * @param {string} props.sectionTitle - Optional section title to display
 * @returns {JSX.Element} Navigation list component
 */
const RenderNavList = ({ List, currentActiveUrl, orgId, isCollapsed, sectionTitle = null }) => {
  return (
    <nav className="">
      {/* Section title (only shown when not collapsed) */}
      {sectionTitle && !isCollapsed && (
        <div className="mb-3">
          <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider px-2">
            {sectionTitle}
          </h3>
        </div>
      )}
      
      {/* Navigation items */}
      <div className="flex flex-col gap-2">
        {List &&
          List?.map((data, index) => (
            <RenderNavItem
              key={index}
              data={data}
              currentActiveUrl={currentActiveUrl}
              orgId={orgId}
              isCollapsed={isCollapsed}
            />
          ))}
      </div>
    </nav>
  );
};

/**
 * Main Navigation Component
 * Provides sidebar navigation with collapsible functionality and organization switching
 * Handles authentication, organization access, and responsive design
 * 
 * @returns {JSX.Element} Main navigation sidebar component
 */
const Navbar = () => {
  
  // State management for navbar functionality
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  
  // Hooks and context
  const { _orgId } = useParams();
  const { _orgId: currOrg } = useParams();
  const { OrgList, isLoading: isLoadingOrgList } = useGetAllOrganization();
  
  // Navigation and location hooks
  const location = useLocation();
  const navigate = useNavigate();
  
  // User authentication data
  const jwtTesting = JSON.parse(sessionStorage.getItem("userData"));

  // URL path parsing for active state - memoized to prevent recalculation
  const urlPath = useMemo(() => location.pathname.split("/"), [location.pathname]);

  /**
   * Toggles navbar collapsed state
   * Handles the expand/collapse functionality of the sidebar
   */
  const changeCollapsedHandler = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  // Authentication check - redirect to sign-in if not authenticated
  useEffect(() => {
    if (jwtTesting === null) {
      sessionStorage.setItem("lastRoute", location.pathname);
      navigate("/sign-in");
    }
  }, [navigate, location.pathname, jwtTesting]);

  // Fetch user details on component mount
  useEffect(() => {
    
    let abortController = new AbortController();
    
    const fetchUserDetails = async () => {
      if (jwtTesting?.user?.id && !userDetails) {
        try {
          const data = await getOnedata("users", jwtTesting.user.id);
          if (!abortController.signal.aborted) {
            // Ensure data is valid before setting
            if (data && typeof data === 'object') {
              setUserDetails(data);
            } else {
              console.warn("Invalid user data received:", data);
              setUserDetails(null);
            }
          }
        } catch (error) {
          if (!abortController.signal.aborted) {
            console.error("Error fetching user details:", error);
            // Set fallback to prevent crashes
            setUserDetails(null);
          }
        }
      }
    };

    fetchUserDetails();

    return () => {
      abortController.abort();
    };
  }, [jwtTesting?.user?.id, userDetails]); // Only depend on the user ID and userDetails state

  // Organization access validation
  useEffect(() => {
    
    if (!isLoadingOrgList && OrgList && OrgList.length > 0 && !OrgList.includes(currOrg)) {
      navigate("*");
    }
  }, [OrgList, currOrg, isLoadingOrgList, navigate]);

  return (
    <div className="flex flex-row w-full max-w-full h-screen overflow-hidden">
        <nav className="h-full">
        <div
          className={`flex flex-col h-full dark:bg-root flex-shrink-0 border-r border-divider bg-inherit px-4 dark:border-gray-600 py-2 ${
            isCollapsed ? "w-16" : "w-64"
          } transition-all duration-300 ease-in-out`}
        >
          {/* Navbar top section - Organization selector and collapse button */}
          <div
            className={`flex gap-4 items-center justify-end ${
              isCollapsed ? "flex-col" : "flex-row"
            } mb-8 flex-shrink-0`}
          >

            
            {/* Organization selection popup */}
            <OrgPopUp
              List={OrgList}
              currOrg={_orgId}
              isCollapsed={isCollapsed}
              userDetails={userDetails}
            />
            
            {/* Collapse/expand button */}
            {!isCollapsed ? (
              <ChevronLeftIcon
                className="size-7 cursor-pointer"
                onClick={changeCollapsedHandler}
              />
            ) : (
              <ChevronRightIcon
                className="size-7 cursor-pointer"
                onClick={changeCollapsedHandler}
              />
            )}
          </div>

          {/* Navbar Mid - Scrollable */}
          <div className="flex-1 overflow-y-auto custom-scrollbar sidebar-scrollbar scroll-smooth">
            <RenderNavList
              List={dashboardSection}
              currentActiveUrl={urlPath[2]}
              orgId={_orgId}
              isCollapsed={isCollapsed}
              sectionTitle="Dashboard"
            />
            <Divider className="my-4" />
            
            <RenderNavList
              List={collateSection}
              currentActiveUrl={urlPath[2]}
              orgId={_orgId}
              isCollapsed={isCollapsed}
              sectionTitle="Collate"
            />
            <Divider className="my-4" />
            
            <RenderNavList
              List={contextualiseSection}
              currentActiveUrl={urlPath[2]}
              orgId={_orgId}
              isCollapsed={isCollapsed}
              sectionTitle="Contextualise"
            />
            <Divider className="my-4" />
            
            <RenderNavList
              List={complySection}
              currentActiveUrl={urlPath[2]}
              orgId={_orgId}
              isCollapsed={isCollapsed}
              sectionTitle="Comply"
            />
            <Divider className="my-4" />
            
            <RenderNavList
              List={adminSection}
              currentActiveUrl={urlPath[2]}
              orgId={_orgId}
              isCollapsed={isCollapsed}
              sectionTitle="Admin & Settings"
            />
            
            {/* Bottom padding for scroll space */}
            <div className="h-4"></div>
          </div>

          {/* Navbar Footer - Fixed */}
          <div className="flex-shrink-0">
            <NavbarFooter
              UserDetails={userDetails}
              _orgId={_orgId}
              isCollapsed={isCollapsed}
            />
          </div>
        </div>
      </nav>

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header component hidden - date/time and notification moved to breadcrumbs */}
        {/* <Header /> */}

        <div className="h-screen overflow-y-auto main-content-scroll">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default React.memo(Navbar);
